
import { computed, defineComponent, onMounted, ref, toRef, watch } from "vue";
import OrderHeaderChangeActions from "@/components/OrderHeaderChangeActions.vue";
import BackAndCancelFooter from "@/components/BackAndCancelFooter.vue";
import ViewHeader from "@/components/ViewHeader.vue";
import { api } from "@/services/Api";
import { useStore } from "@/store";
import {
  Coupon,
  Price,
  Slot,
  PriceLine,
  GetProductSlotsResponse,
} from "@/models/Product";
import Swal from "sweetalert2";
import { Slot as CalendarSlot } from '@/models/Calendar';
import { useRouter, useRoute } from "vue-router";
import moment from "moment";
import { useI18n } from "vue-i18n";
import Q from "q";
import _ from "lodash";
import { CouponLimitationType } from "@/models/Enums";
import TextInput from "@/components/Inputs/TextInput.vue";
import { ApiResult } from "@/services/ApiResult";
import { VIRTUAL_SLOT_ID, FAKE_SLOT_ID_PREFIX } from '@/models/Constants';

export default defineComponent({
  name: "ProductPriceLines",
  props: {
    productId: {
      type: Number,
      required: true,
    },
    dateStart: {
      type: String,
      required: false,
    },
    slotId: {
      type: Number,
      required: false,
    },
    editMode: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  components: {
    OrderHeaderChangeActions,
    BackAndCancelFooter,
    ViewHeader,
    TextInput
  },
  async setup(props) {
    const { t } = useI18n();
    const editMode = toRef(props, "editMode");
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const priceLists = ref<Array<Price>>();
    const slot = ref<Slot>();
    const date = ref<string>();
    const dateString = ref("");
    const priceSearchNumber = ref("");
    const couponLimitationType = CouponLimitationType;
    const showAllOrderPriceLines = computed(
      () => store.state.order.showAllPriceLines
    );
    const coupons = computed(
      () => store.getters.getCouponsByOrderBusinessClient as Coupon[]
    );
    const clubId = computed(() => {
      return store.state.order?.businessClientId || 0;
    });
    const isClub = computed(() => clubId.value > 0);
    const couponsForCurrentProduct = computed(() =>
      coupons.value.filter((x) => x.productIds.includes(Number(route.params.productId)))
    );
    const orderPriceTypeId = computed(() => store.state.order?.priceType || 0);
    const showClubMoreBtn = computed(
      () => isClub.value && clubId.value > 0 && !showAllOrderPriceLines.value
    );
    const priceListsToView = computed(() => {
      if (isClub.value && !showAllOrderPriceLines.value) {
        if (!priceLists.value) {
          return [];
        }

        return priceLists.value.map((x) => {
          return {
            id: x.id,
            productId: x.productId,
            priceTypeId: x.priceTypeId,
            fromDate: x.fromDate,
            toDate: x.toDate,
            title: x.title,
            remarks: x.remarks,
            officialPrice: x.officialPrice,
            currencyId: x.currencyId,
            pricePerPerson: x.pricePerPerson,
            channelId: x.channelId,
            channelName: x.channelName,
            productPriceId: x.productPriceId,
            priceLines: x.priceLines.filter((p) => (p.barcode ?? "").includes(priceSearchNumber.value)).filter((p) => p.coupon != null),
          };
        }).filter(x => x.priceLines.some(_ => true));
      }

      return priceLists.value?.map(x => {
        return {
          ...x,
          priceLines: x.priceLines.filter((p) => (p.barcode ?? "").includes(priceSearchNumber.value))
        }
      }).filter(x => x.priceLines.some(_ => true));
    });
    const activePriceId = ref<number | null>(null);
    const busketProducts = computed(() => store.state.order.products);
    const selectedProduct = computed(() => store.state.selectedProduct);

    watch(() => busketProducts, () => {
      if (busketProducts.value.length > 0 && selectedProduct.value) {
        const matchingProduct = busketProducts.value.find(
          (product) => product.slotId === selectedProduct.value?.slotId 
                      && product.date === selectedProduct.value?.date 
                      && product.slotEndTime === selectedProduct.value?.slotEndTime 
                      && product.slotStartTime === selectedProduct.value?.slotStartTime 
        );
        if (matchingProduct) {
          activePriceId.value = matchingProduct.priceId;
        } else {
          activePriceId.value = null;
        }
      } else {
        activePriceId.value = null;
      }
    }, { deep: true });

    watch(
      () => route.params.productId,
      async () => {
        await Q.delay(100);

        if (route.name == "select-products") {
          return;
        }
        if (!route.params.productId) {
          return;
        }

        Swal.showLoading();
        await Q.delay(200);
        if (route.params.dateStart) {
          dateString.value = moment(route.params.dateStart, "YYYYMMDD").format(
            "YYYY-MM-DD"
          );
          date.value = dateString.value;
        } else {
          dateString.value = moment().format("YYYY-MM-DD");
          date.value = dateString.value;
        }
        priceLists.value = undefined;
        var promiseResult = updatePrice();
        await Q.delay(400);
        await promiseResult;
        Swal.close();
      }
    );

    const product = computed(() =>
      store.state.products.find((x) => x.id == Number(route.params.productId))
    );

    onMounted(async () => {
      await Q.delay(100);
      if (route.params.dateStart) {
        dateString.value = moment(route.params.dateStart, "YYYYMMDD").format(
          "YYYY-MM-DD"
        );
        date.value = dateString.value;
      } else {
        dateString.value = moment().format("YYYY-MM-DD");
        date.value = dateString.value;
      }
      return await updatePrice();
    });

    async function updatePrice(): Promise<boolean> {
      Swal.showLoading();
      await Q.delay(100);
      store.commit("setSelectedProduct", {
        productId: null,
      });
      if (route.params.dateStart === moment().format("YYYY-MM-DD")) {
        if(clubId.value){
          const loadPricesResult = await api.loadPricesByBusinessClient(route.params.dateStart, clubId.value);
          store.commit("updatePreloadPrices", loadPricesResult.data);
        }else{
          const filteredPricesByPriceType = store.state.preloaded?.prices.prices.filter(p => p.priceTypeId == store.state.order.priceType) ?? [];
          const filteredPrices = filteredPricesByPriceType.filter(p => !p.businessClientId && !p.businessClientTypeId);
          store.commit("updatePreloadPrices", { prices: filteredPrices });
        }

        slot.value = store.getters.preloadedSlots.slots.find(
          (x) => x.productId == route.params.productId
        );
        priceLists.value = _.cloneDeep(
          store.getters.preloadedPrices.prices.filter(
            (x) =>
              x.productId == route.params.productId &&
              x.priceTypeId == store.state.order.priceType
          )
        );
        priceLists.value?.forEach((price) => {
          price.priceLines = price.priceLines.map((pl) => {
            const priceLine = Object.assign(new PriceLine(), pl);
            priceLine.quantity = 0;
            priceLine.discount = 0;
            return priceLine;
          });
        });

        if (route.params.slotId && route.params.slotId.toString().startsWith(FAKE_SLOT_ID_PREFIX)) {
          const storeSlot: CalendarSlot = await store.dispatch("getVirtualSlot", route.params.slotId);
            slot.value = {
              id: VIRTUAL_SLOT_ID,
              isVirtual: true,
              endTime: storeSlot.endTime,
              productId: Number(route.params.productId),
              quantity: 0,
              startTime: storeSlot.startTime,
              duration: storeSlot.duration,
              slotFakeId: storeSlot.slotFakeId
            }
        }
        else if (route.params.slotId) {
          let foundSlot = store.getters.preloadedSlots.slots.find((x) => x.slot?.id == Number(route.params.slotId) || x.id == Number(route.params.slotId));

          if (foundSlot && foundSlot?.slot) {
            slot.value = _.cloneDeep(foundSlot.slot);
          } else if(foundSlot && foundSlot?.id) {
            slot.value = _.cloneDeep(foundSlot);
          }
        } else if (route.params.productId) {
          slot.value = _.cloneDeep(
            store.getters.preloadedSlots.slots.find(
              (x) => x.productId == route.params.productId
            )
          );
        }
      }
      
      if (!priceLists.value && route.params.productId) {
        const priceResponse = await api.getProductPrices(
          Number(route.params.productId),
          store.state.order.priceType,
          dateString.value,
          clubId.value
        );

        priceLists.value = priceResponse.data?.prices ?? [];
        priceLists.value?.forEach((price) => {
          price.priceLines = price.priceLines.map((pl) => {
            const priceLine = Object.assign(new PriceLine(), pl);
            priceLine.quantity = 0;
            priceLine.discount = 0;
            return priceLine;
          });
        });
      }

      if (!slot.value && route.params.productId) {
        let slotsResponse: ApiResult<GetProductSlotsResponse> = new ApiResult<GetProductSlotsResponse>(null, undefined);
        if (!route.params.slotId?.toString().startsWith(FAKE_SLOT_ID_PREFIX) && route.params.productId) {
          slotsResponse = await api.getProductSlots(
            Number(route.params.productId),
            [store.state.order.priceType],
            dateString.value,
            0
          );

          if (slotsResponse.data == null || !slotsResponse.data?.slots?.filter(x => !x.isVirtual).length) {
            priceLists.value = [];
            await slotNotFound();
            return false;
          }
        }

        if (route.params.slotId) {
          if (route.params.slotId.toString().startsWith(FAKE_SLOT_ID_PREFIX)) {
            const storeSlot: CalendarSlot = await store.dispatch("getVirtualSlot", route.params.slotId);
            slot.value = {
              id: VIRTUAL_SLOT_ID,
              isVirtual: true,
              endTime: storeSlot.endTime,
              productId: Number(route.params.productId),
              quantity: 0,
              startTime: storeSlot.startTime,
              duration: storeSlot.duration,
              slotFakeId: storeSlot.slotFakeId
            }
          } else if(slotsResponse.data) {
            slot.value = slotsResponse.data.slots.find(
              (x) => x.id == Number(route.params.slotId)
            );
          }
        } else if(slotsResponse.data) {
          slot.value = slotsResponse.data.slots.filter(x => !x.isVirtual)[0];
        }
      }

      date.value = date.value ?? moment().format("YYYY-MM-DD");
      store.commit("setSelectedProduct", {
        date: date.value,
        productId: product.value?.id,
        slot: slot.value,
      });
      if (isClub.value && priceLists.value) {
        for (let i = 0; i < priceLists.value.length; i++) {
          const priceList = priceLists.value[i];

          if (priceList.priceTypeId != orderPriceTypeId.value) {
            continue;
          }

          priceList.priceLines.forEach((priceLine) => {
            let coupon = couponsForCurrentProduct.value.find(
              (x) =>
                x.customerTypes.includes(priceLine.id) &&
                moment(x.validDateFrom).isSameOrBefore(
                  moment(route.params.dateStart)
                ) &&
                moment(x.validDateTo).isSameOrAfter(moment(route.params.dateStart))
            );
            if (coupon) {
              priceLine.coupon = coupon;
              priceLine.couponId = coupon.id;
            }
          });
        }
      }
      Swal.close();
      return true;
    }

    function selectSlot() {
      router.replace({
        name: "calendar",
        params: {
          productId: route.params.productId,
        },
      });
    }

    async function slotNotFound() {
      const result = await Swal.fire({
        text: t("cashier.alert.slot-not-found"),
        icon: "error",
        showCancelButton: true,
        cancelButtonText: t("cashier.label.no"),
        confirmButtonText: t("cashier.label.yes"),
        customClass: {
          confirmButton: "popup-bookit-button",
          cancelButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
      });
      if (result.isDismissed) {
        if (isClub.value) {
          router.push({
            name: "clubProducts",
            params: {
              clubId: clubId.value,
            },
          });
        }
        return;
      }
      selectSlot();
    }

    async function selectPriceLine(
      priceLine: PriceLine,
      channelId,
      priceId,
      currencyId
    ) {
      if(activePriceId.value !== null && activePriceId.value !== priceId) {
        return;
      }
      
      const canContinue = await store.dispatch(
        "CouponClaculateWhenAddProduct",
        {
          priceLine,
        }
      );
      if (!canContinue) {
        return;
      }
      const plainClone = _.cloneDeep(priceLine);
      const clonePriceLine: PriceLine = Object.setPrototypeOf(plainClone, PriceLine.prototype);
      if (editMode.value) {
        await store.dispatch("changePriceLineInBusketProduct", {
          productId: route.params.productId,
          priceLine: clonePriceLine,
          slot: slot.value,
          date: date.value,
          channelId,
          priceId,
          currencyId,
        });
        // router.back();
        const parms = JSON.parse(
          JSON.stringify(router.currentRoute.value.params)
        );
        router.replace({
          name: "productPriceLines",
          params: parms,
          query: router.currentRoute.value.query,
        });
        return;
      }
      
      store.commit("updateOrderProduct", {
        productId: route.params.productId,
        priceLine: clonePriceLine,
        slot: slot.value,
        date: date.value,
        channelId,
        priceId,
        currencyId,
      });
    }

    function currencySign(id) {
      return store.state.currencies.find((x) => x.id == id)?.symbol;
    }

    function showAllPriceLines() {
      store.commit("showAllOrderPriceLines");
    }

    function onSearchEnterPress() {
      if (priceListsToView.value?.length == 1 && priceListsToView.value[0].priceLines.length == 1) {
        const priceList = priceListsToView.value[0];
        const priceLine = priceList.priceLines[0];

        selectPriceLine(
          priceLine,
          priceList.channelId,
          priceList.id,
          priceList.currencyId
        );
      }
    }

    return {
      priceLists,
      selectPriceLine,
      product,
      slot,
      currencySign,
      coupons,
      clubId,
      showClubMoreBtn,
      priceListsToView,
      showAllOrderPriceLines,
      showAllPriceLines,
      isClub,
      couponLimitationType,
      priceSearchNumber,
      onSearchEnterPress,
      activePriceId
    };
  },
});
